import { useState } from "react";
import { Link } from "react-router-dom";
import { BellRing, Megaphone, Plus, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Card, EmptyState, LoadingState, ErrorState, StatusBadge, Field, SubmitButton, FormError } from "@/components/common";
import { PlanGate } from "@/components/PlanGate";
import { DueText, ProposeDialog, ProposalSummary, NewMaintenanceDialog } from "@/modules/followups/shared";
import { MessageCustomerButton } from "@/modules/messaging/Inbox";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { t } from "@/i18n";

const FILTERS = ["open", "overdue", "upcoming", "proposals", "closed"];
const match = (i, f) => (f === "closed" ? i.status !== "open" : i.status === "open" && (f === "open" || (f === "overdue" && i.due.overdue)
  || (f === "upcoming" && !i.due.overdue) || (f === "proposals" && i.proposals.some((p) => p.status === "pending"))));

function Detail({ item, onClose, reload }) {
  const { garage, readOnly } = useGarage();
  const [propose, setPropose] = useState(false);
  const put = async (body) => {
    try { await api.put(`/followups/maintenance/${item.id}`, body); reload(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="followup-detail">
        <DialogHeader><DialogTitle>{item.customer.name} · {item.vehicle?.plate}</DialogTitle><DialogDescription>{item.recommendation}</DialogDescription></DialogHeader>
        <DueText due={item.due} className="text-sm" />
        <dl className="grid grid-cols-2 gap-2 text-sm">
          <dt className="text-slate-500">{t("fu.reminders")}</dt><dd data-testid="followup-reminder-count">{item.reminder_count}{item.stopped_reason && ` · ${t(`fu.stopped.${item.stopped_reason}`)}`}</dd>
          <dt className="text-slate-500">{t("fu.lastContact")}</dt><dd>{item.last_contact_at ? fmtDateTime(item.last_contact_at, garage.timezone) : "—"}</dd>
          <dt className="text-slate-500">{t("fu.nextReminder")}</dt><dd>{item.next_reminder_at ? fmtDateTime(item.next_reminder_at, garage.timezone) : "—"}</dd>
        </dl>
        {item.reminders.length > 0 && <ul className="space-y-1 text-xs text-slate-600" data-testid="followup-reminder-log">{item.reminders.map((r, i) => <li key={i}>{fmtDateTime(r.at, garage.timezone)} — {r.channels.map((c) => t(`fu.channel.${c}`)).join(", ") || t("fu.channel.none")}</li>)}</ul>}
        <div className="space-y-2">{item.proposals.map((p) => <ProposalSummary key={p.id} p={p} tz={garage.timezone} />)}</div>
        {!readOnly && item.status === "open" && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setPropose(true)} data-testid="followup-propose-button">{t("fu.propose")}</Button>
            <MessageCustomerButton vehicleId={item.vehicle?.id} />
            <label className="flex items-center gap-2 text-sm"><Switch checked={item.reminders_enabled} onCheckedChange={(v) => put({ reminders_enabled: v })} data-testid="followup-reminders-switch" /> {t("fu.remindersOn")}</label>
            <Button size="sm" variant="outline" onClick={() => put({ status: "done" })} data-testid="followup-done-button">{t("fu.markDone")}</Button>
            <Button size="sm" variant="ghost" onClick={() => put({ status: "dismissed" })} data-testid="followup-dismiss-button">{t("fu.dismiss")}</Button>
          </div>
        )}
        {propose && <ProposeDialog item={item} onClose={() => setPropose(false)} onSaved={() => { setPropose(false); reload(); onClose(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function PromoDialog({ onClose }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setRes((await api.post("/followups/promotional", { text })).data); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="promo-dialog">
        <DialogHeader><DialogTitle>{t("fu.promo")}</DialogTitle><DialogDescription>{t("fu.promoHint")}</DialogDescription></DialogHeader>
        {res ? <p className="text-sm" data-testid="promo-result">{t("fu.promoResult", { n: res.recorded, skipped: res.skipped_no_consent })}</p> : (
          <form onSubmit={submit} className="space-y-3"><FormError message={error} />
            <Field label={t("msg.message")}><Textarea required minLength={3} value={text} onChange={(e) => setText(e.target.value)} data-testid="promo-text-input" /></Field>
            <SubmitButton busy={busy} data-testid="promo-submit">{t("fu.promoRecord")}</SubmitButton></form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewForCustomer({ onClose, onSaved }) {
  const [c, setC] = useState("");
  const [v, setV] = useState("");
  const customers = useFetch("/customers");
  const vehicles = useFetch("/vehicles", { customer_id: c || "none" });
  if (c && v) return <NewMaintenanceDialog customerId={c} vehicleId={v} onClose={onClose} onSaved={onSaved} />;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-followup-pick">
        <DialogHeader><DialogTitle>{t("fu.setNext")}</DialogTitle><DialogDescription>{t("fu.pickVehicle")}</DialogDescription></DialogHeader>
        <Select value={c} onValueChange={setC}><SelectTrigger data-testid="new-followup-customer"><SelectValue placeholder={t("msg.pickCustomer")} /></SelectTrigger>
          <SelectContent>{(customers.data || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.first_name} {x.last_name}</SelectItem>)}</SelectContent></Select>
        {c && <Select value={v} onValueChange={setV}><SelectTrigger data-testid="new-followup-vehicle"><SelectValue placeholder={t("sched.vehicle")} /></SelectTrigger>
          <SelectContent>{(vehicles.data || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.plate} · {x.make} {x.model}</SelectItem>)}</SelectContent></Select>}
      </DialogContent>
    </Dialog>
  );
}

function Row({ i, onOpen, tz }) {
  const pending = i.proposals.find((p) => p.status === "pending");
  return (
    <button type="button" onClick={onOpen} data-testid={`followup-row-${i.id}`}
      className="grid w-full gap-2 border-b border-slate-100 px-4 py-3 text-left text-sm transition-colors duration-150 hover:bg-slate-50 md:grid-cols-[1.3fr_1.6fr_1.2fr_0.8fr_1fr]">
      <span><strong className="block">{i.customer.name}</strong><span className="text-xs text-slate-500">{i.vehicle?.plate} · {i.vehicle?.make} {i.vehicle?.model}</span></span>
      <span className="text-slate-700">{i.recommendation}</span>
      <DueText due={i.due} />
      <span className="text-xs text-slate-600">{t("fu.remindersN", { n: i.reminder_count })}<br />{i.last_contact_at ? fmtDateTime(i.last_contact_at, tz) : t("fu.noContact")}</span>
      <span className="flex flex-wrap items-start gap-1">{i.status !== "open" ? <StatusBadge status={i.status} /> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700" data-testid={`followup-next-action-${i.id}`}>{t(`fu.actions.${i.next_action}`)}</span>}
        {pending && <StatusBadge status="pending" />}</span>
    </button>
  );
}

function FollowUpsInner() {
  const { garage, readOnly } = useGarage();
  const { data, loading, error, reload } = useFetch("/followups");
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(null);
  const [dlg, setDlg] = useState(null);
  const run = async () => {
    try { const r = (await api.post("/followups/run-reminders")).data; toast.success(t("fu.runResult", { n: r.reminded, stopped: r.stopped_booked + r.stopped_max })); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const items = data.items.filter((i) => match(i, filter));
  const cur = open && data.items.find((i) => i.id === open);
  return (
    <div>
      <PageHeader title={t("fu.title")} subtitle={t("fu.subtitle")} actions={!readOnly && <>
        <Button variant="outline" onClick={run} data-testid="run-reminders-button"><BellRing className="mr-2 h-4 w-4" /> {t("fu.runNow")}</Button>
        <Button variant="outline" onClick={() => setDlg("promo")} data-testid="promo-button"><Megaphone className="mr-2 h-4 w-4" /> {t("fu.promo")}</Button>
        <Button onClick={() => setDlg("new")} data-testid="new-followup-button"><Plus className="mr-2 h-4 w-4" /> {t("fu.setNext")}</Button></>} />
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {["overdue", "upcoming", "unanswered_proposals", "to_follow_up"].map((k) => (
          <Card key={k} testId={`fu-summary-${k}`}><p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t(`fu.summary.${k}`)}</p><p className="mt-1 text-3xl font-extrabold text-slate-900">{data.summary[k]}</p></Card>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap gap-2">{FILTERS.map((k) => <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`fu-filter-${k}`}
        className={`rounded-full border px-3 py-1 text-xs font-semibold ${filter === k ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{t(`fu.filters.${k}`)}</button>)}</div>
      <div className="gf-card overflow-hidden" data-testid="followups-list">
        {items.length === 0 ? <div className="p-6"><EmptyState icon={ClipboardList} title={t("fu.empty")} text={t("fu.emptyText")} /></div>
          : items.map((i) => <Row key={i.id} i={i} tz={garage.timezone} onOpen={() => setOpen(i.id)} />)}
      </div>
      <p className="mt-3 text-xs text-slate-500">{t("fu.simNote")} <Link to="/pro/settings?tab=reminders" className="font-semibold text-teal-700">{t("fu.rulesLink")}</Link></p>
      {cur && <Detail item={cur} onClose={() => setOpen(null)} reload={reload} />}
      {dlg === "promo" && <PromoDialog onClose={() => setDlg(null)} />}
      {dlg === "new" && <NewForCustomer onClose={() => setDlg(null)} onSaved={() => { setDlg(null); reload(); }} />}
    </div>
  );
}

export default function FollowUps() {
  return <PlanGate feature="follow_ups" managers><FollowUpsInner /></PlanGate>;
}

export function FollowUpsWidget() {
  const { hasFeature, isManager } = useGarage();
  const show = isManager && hasFeature("follow_ups");
  const { data } = useFetch(show ? "/followups" : null);
  if (!show || !data) return null;
  const top = data.items.filter((i) => i.status === "open").sort((a, b) => Number(b.due.overdue) - Number(a.due.overdue)).slice(0, 4);
  return (
    <Card testId="dashboard-followups" className="min-w-0">
      <div className="mb-3 flex items-center justify-between"><h2 className="text-lg font-bold text-slate-900">{t("fu.widget")}</h2><Link to="/pro/followups" className="text-sm font-semibold text-teal-700" data-testid="dashboard-open-followups">{t("fu.openAll")}</Link></div>
      <p className="mb-2 text-xs text-slate-500">{t("fu.widgetSummary", { overdue: data.summary.overdue, follow: data.summary.to_follow_up, pending: data.summary.unanswered_proposals })}</p>
      {top.map((i) => <Link key={i.id} to="/pro/followups" className="block border-t border-slate-100 py-2 text-sm"><strong>{i.customer.name}</strong> · {i.recommendation}<DueText due={i.due} /></Link>)}
      {top.length === 0 && <p className="text-sm text-slate-500">{t("fu.empty")}</p>}
    </Card>
  );
}
